import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StatusSelectionModal = ({ isOpen, onClose, onConfirm, currentStatus }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedStatus) {
      onConfirm(selectedStatus, notes);
      setSelectedStatus('');
      setNotes('');
      onClose();
    }
  };

  const statusOptions = [
    { value: 'processing', label: 'Beantragt', icon: 'Clock', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { value: 'approved', label: 'Genehmigt', icon: 'CheckCircle2', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
    { value: 'rejected', label: 'Abgelehnt', icon: 'XCircle', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' }
  ];

  // Helper function to get allowed status transitions based on current status
  const getAllowedTransitions = (status) => {
    const transitions = {
      'unbestätigt': ['processing'], // Can only go to beantragt
      'bestätigt': ['processing'], // Can only go to beantragt
      'processing': ['approved', 'rejected'], // Can go to approved or rejected
      'approved': ['redeemed'], // Can only go to redeemed
      'rejected': [], // Final state - no transitions
      'redeemed': [] // Final state - no transitions
    };
    return transitions?.[status] || [];
  };

  // Filter status options based on current application status
  const getAvailableStatusOptions = () => {
    if (!currentStatus) {
      return statusOptions;
    }

    const allowedTransitions = getAllowedTransitions(currentStatus);

    // Filter status options to only show allowed transitions
    return statusOptions?.filter(option => allowedTransitions?.includes(option?.value));
  };

  const availableOptions = getAvailableStatusOptions();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="GitBranch" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Status auswählen
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Schließen"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Neuer Status
            </label>
            <div className="space-y-3">
              {availableOptions?.length > 0 ? (
                availableOptions?.map((option) => (
                  <button
                    key={option?.value}
                    onClick={() => setSelectedStatus(option?.value)}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-smooth ${
                      selectedStatus === option?.value
                        ? `${option?.borderColor} ${option?.bgColor}`
                        : 'border-border bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <Icon
                      name={option?.icon}
                      size={20}
                      color={selectedStatus === option?.value ? `var(--color-${option?.color?.split('-')?.[1]}-${option?.color?.split('-')?.[2]})` : 'var(--color-muted-foreground)'}
                    />
                    <span className={`font-medium ${
                      selectedStatus === option?.value ? option?.color : 'text-muted-foreground'
                    }`}>
                      {option?.label}
                    </span>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">
                    {currentStatus === 'rejected' && 'Abgelehnte Anträge können nicht geändert werden (Endstatus).'}
                    {currentStatus === 'redeemed' && 'Eingelöste Anträge können nicht geändert werden (Endstatus).'}
                    {!['rejected', 'redeemed']?.includes(currentStatus) && 'Keine gültigen Statusübergänge verfügbar.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-2">
              Notizen (optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Fügen Sie hier Notizen hinzu..."
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} iconName="">
            Abbrechen
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            disabled={!selectedStatus}
            iconName=""
          >
            Bestätigen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StatusSelectionModal;